import { Component } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"

import logAction from "../../utils/log_action"

import bookIcon from "images/partners/book.svg"
import { queryAdd, queryUpdate, queryDelete, queryClone } from "./gql"

class QueryForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      query: {
        id: props.query.id || "",
        name: props.query.name || "",
        module_name: props.query.module_name || "",
        description: props.query.description || "",
      },
      create: props.create,
      errors: {},
      submitting: false,
    }
  }

  handleErrors = errors => {
    const errorsToHash = errors.reduce((acc, cur) => {
      acc[cur.attribute] = `${cur.attribute} ${cur.message}`
      return acc
    }, {})
    if (errors && errors.length > 0) {
      this.setState({ errors: errorsToHash })
      throw errors
    } else {
      this.setState({ errors: {} })
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.create !== this.state.create) {
      this.setState({ create: nextProps.create })
    }
    if (nextProps.query !== this.state.query) {
      this.setState({ query: nextProps.query })
    }
  }

  handleInputChange = event => {
    let { name, value } = event.target
    let query = { ...this.state.query, [name]: value }
    this.setState({ query })
  }

  handleSubmit = event => {
    event.preventDefault()
    this.state.create ? this.handleCreate() : this.handleUpdate()
  }

  handleCreate = () => {
    this.setState({ submitting: true })
    return queryAdd(this.props.service.slug, this.state.query)
      .then(({ query, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        logAction("create_query")
        this.props.updateRouter("edit_replace", query)
        this.props.updateQueries("add", query)
        window.appendFlash("Query created")
      })
      .catch(errs => {
        window.appendFlash(
          "Could not create query — please check your data",
          "danger"
        )
        this.setState({ submitting: false })
      })
  }

  handleUpdate = () => {
    this.setState({ submitting: true })
    return queryUpdate(this.state.query)
      .then(({ query, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        logAction("edit_query")
        this.props.updateRouter("edit_replace", query)
        this.props.updateQueries("update", query)
        window.appendFlash("Query updated")
      })
      .catch(errs => {
        window.appendFlash(
          "Could not update query — please check your data",
          "danger"
        )
        this.setState({ submitting: false })
      })
  }

  handleDelete = () => {
    this.setState({ submitting: true })
    return queryDelete(this.state.query)
      .then(({ errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        logAction("delete_query")
        this.props.updateRouter("index")
        this.props.updateQueries("delete", this.state.query)
        window.appendFlash("Query deleted")
      })
      .catch(errs => {
        this.setState({ submitting: false })
        window.appendFlash(
          "Could not delete query — please check your data",
          "danger"
        )
      })
  }

  handleClone = () => {
    this.setState({ submitting: true })
    return queryClone(this.state.query)
      .then(({ query, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        logAction("clone_query")
        this.props.updateRouter("edit", query)
        window.appendFlash("Query cloned")
      })
      .catch(errs => {
        this.setState({ submitting: false })
        window.appendFlash(
          "Could not clone query — please check your data",
          "danger"
        )
      })
  }

  confirmClone = event => {
    event.preventDefault()
    if (
      window.confirm(
        "Are you sure you want to clone this query? It will clone all the properties, including query fields, and ingredients"
      )
    ) {
      return this.handleClone()
    }
  }

  confirmDelete = event => {
    event.preventDefault()
    if (
      window.confirm(
        "Are you sure you want to PERMANENTLY delete this query? Any Applets that use this query will also be deleted."
      )
    ) {
      return this.handleDelete()
    }
  }

  render() {
    let { query } = this.state
    return (
      <>
        <div className="page-header">
          <h1>
            {this.state.create ? "Create new query" : this.props.query.name}
          </h1>
        </div>

        {this.props.service.is_published && (
          <h6>
            This service is published and cannot be modified. If you need to
            make changes, clone the service, apply the changes, and then submit
            your staging service for review.
          </h6>
        )}
        <form
          className={classnames("form", { "service-edit": !this.props.create })}
          onSubmit={this.handleSubmit}
        >
          <section className="form-section">
            <div className="field">
              <div className="label">
                <label htmlFor="name">Name</label>
              </div>
              <div className="input">
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={query.name}
                  onChange={this.handleInputChange}
                  required
                />
              </div>

              {this.state.errors.name && (
                <FormError errorText={this.state.errors.name} />
              )}
              <p className="note">
                Use sentence case capitalization — no period
              </p>
            </div>

            <div className="field">
              <div className="label">
                <label htmlFor="description">Description</label>
              </div>

              <div className="input">
                <textarea
                  id="description"
                  type="text"
                  name="description"
                  value={query.description}
                  onChange={this.handleInputChange}
                  required
                />
              </div>

              {this.state.errors.description && (
                <FormError errorText={this.state.errors.description} />
              )}
              <p className="note">
                Must be a complete sentence starting with “This query returns”
                and ending with a period
              </p>
            </div>

            <div className="field">
              <div className="label">
                <label htmlFor="module_name">Endpoint</label>
              </div>

              <div className="input-group">
                <span className="input-group-addon">
                  {`${this.props.protocolUrl}/`}
                </span>
                <input
                  id="module_name"
                  type="text"
                  name="module_name"
                  value={query.module_name}
                  minLength={4}
                  pattern="^[a-zA-Z0-9-_]+$"
                  className="input-group-input"
                  onChange={this.handleInputChange}
                  required
                />
              </div>

              {this.state.errors.module_name && (
                <FormError errorText={this.state.errors.module_name} />
              )}
              <p className="note">Must be in snake_case.</p>
            </div>
          </section>
          <div className="actions">
            {!this.props.service.is_published && !this.state.create && (
              <div className="action-left">
                <a
                  href="#"
                  title="Permanently delete this query?"
                  className="secondary-action delete"
                  onClick={this.confirmDelete}
                >
                  Delete query
                </a>
                <a
                  href="#"
                  title="Clone all the properties, including query fields?"
                  className="secondary-action clone"
                  onClick={this.confirmClone}
                >
                  Clone query
                </a>
              </div>
            )}
            <div className="action-right">
              <button
                type="submit"
                className="button-secondary"
                disabled={
                  this.props.service.is_published || this.state.submitting
                }
              >
                {this.state.submitting ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </>
    )
  }
}

const FormError = ({ errorText }) => (
  <span className="error-message">Error: {errorText}</span>
)

QueryForm.propTypes = {
  updateRouter: PropTypes.func.isRequired,
  updateQueries: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
  protocolUrl: PropTypes.string.isRequired,
  queriesUrl: PropTypes.string.isRequired,
  query: PropTypes.object,
  create: PropTypes.bool,
}

QueryForm.defaultProps = {
  create: false,
  query: {},
}

export default QueryForm
